/**
 * A FAILED mapping is not a NOT-SUBMITTED decision.
 *
 * WHAT WENT WRONG (outside review, 2026-10-19, two Clinical Dev studies on
 * gemini-3.8-flash). When the batched mapper produced no usable answer for a
 * field (the call failed or timed out, the field was missing from the answer,
 * or the answer was rejected by validation), the generator stored the row as
 * `scenario: "not_submitted"`, `origin: "Not Submitted"` with the rationale
 * "Gemini batched mapper did not return a result after retries". 84 of 323
 * model-mapped fields on NFP-HTN-302 and 88 of 297 on KBL-UC-204 were stored
 * that way, among them CMDOSFRM, CMPRIOR, CMONGO, serious adverse event fields
 * and ICLANG. Every reader then took the placeholder for a decision: the
 * Define-XML and SDRG left the field out as deliberately not submitted, the
 * K5 check counted it as "correctly marked NOT SUBMITTED", and S3 compared the
 * data dictionary's real assignment against it and reported a divergence the
 * mapping never decided.
 *
 * THE RULE THIS MODULE HOLDS. A row the mapper failed on carries its own
 * state, `scenario: "mapping_failed"` with the flag `mapping_failed`, and every
 * reader asks THIS module rather than comparing the scenario string itself.
 *
 * LEGACY ROWS. Rows written before this change still say `not_submitted`. They
 * are recognised by the one mark only the old failure branch ever wrote: the
 * "did not return a result after retries" rationale. So a study does not have
 * to be regenerated for its stored mapping to be read correctly.
 *
 * NOT by the `unmapped` flag alone, although that branch wrote it too.
 *
 * LEGACY NORMALISER ROWS (Jim, 2026-10-19, decisions A and B). Before
 * 2026-09-25 the domain normaliser (normalizeDomain.ts) ran over EVERY row and
 * overwrote the reason of any row with a blank or invalid domain with
 * `Domain string was not a valid SDTM domain code ... Original mapper output:
 * domain="<d>", variable="<v>"`. The "Original mapper output" says what the
 * row held before the overwrite, so it tells the cases apart:
 *
 *   A. blank domain AND variable, source "ai": the FAILURE branch's row. From
 *      2026-04-08 (55de8735) to 2026-10-19 the required-fields check rejected
 *      every model answer with a blank domain, so a blank "ai" row could only
 *      be a failure. REFERENCE-A holds 74; an earlier note here called them
 *      model answers, which was wrong (independent checker, 2026-10-19).
 *   B. a non-blank original domain or variable: a real model answer whose
 *      domain is not a valid SDTM domain. Nothing decided to leave the field
 *      out, so it needs review like a failure (decision B). REFERENCE-A holds 46.
 *   Neither: blank original on a "pre_filter" or "lookup" row. The old
 *      normaliser overwrote those deliberate exclusions too (one per study on
 *      the Clinical Dev census); they stay decisions.
 *
 * A model answer with an invalid domain is now stored by the normaliser as
 * `mapping_failed` with the extra flag `invalid_domain` (isInvalidDomainRow).
 */
// Deliberately imports nothing: the web panel imports this module directly, and
// the model types pull server-only Firestore types into the web build.
// The row builder lives in mappingFailedRow.ts.

/** The scenario value of a row the mapper produced no usable answer for. NOT a CDISC scenario. */
export const MAPPING_FAILED_SCENARIO = "mapping_failed" as const;
/** The validation flag every failed row carries. */
export const MAPPING_FAILED_FLAG = "mapping_failed";
/** The rationale the pre-2026-10-19 failure branch wrote. */
export const LEGACY_FAILED_RATIONALE = "did not return a result after retries";
/** Plain-language label for the state, used by exports and checks. */
export const MAPPING_FAILED_LABEL = "Mapping failed (needs review)";
/**
 * A not_submitted that is the mapping MODEL's own answer ("no SDTM
 * equivalent"), not a decision by the pre-filter, the library or a person.
 * It is a proposal awaiting confirmation (lead, 2026-10-19): visibly marked on
 * the row, a WARN in K5, never "deliberate" in S3, under Needs Review.
 */
export const MODEL_PROPOSAL_MARK = "model-proposal";
export const MODEL_PROPOSAL_FLAG = "model_not_submitted_proposal";
export const MODEL_PROPOSAL_LABEL = "Not submitted: proposed by the mapping model (awaiting confirmation)";

type RowLike = Record<string, unknown> | null | undefined;

const str = (v: unknown): string => (v == null ? "" : String(v)).trim();

function flagsOf(row: Record<string, unknown>): string[] {
  return Array.isArray(row.validationFlags) ? (row.validationFlags as unknown[]).map((f) => str(f)) : [];
}

/** The flag on a model answer whose SDTM domain is not a valid SDTM domain (decision B). */
export const INVALID_DOMAIN_FLAG = "invalid_domain";
/** Plain-language label for an invalid-domain answer. */
export const INVALID_DOMAIN_LABEL = "Mapping failed: not a valid SDTM domain (needs review)";
/** The stamp the pre-2026-09-25 domain normaliser wrote over a row's reason. */
export const LEGACY_NORMALISER_RATIONALE = "Domain string was not a valid SDTM domain code";

/**
 * The model's original domain and variable, as recorded in the rationale by the
 * normaliser (legacy stamp, and the current invalid-domain reason, which keeps
 * the same `domain="…", variable="…"` form). Null when the row carries neither.
 */
export function recordedMapperOutput(row: RowLike): { domain: string; variable: string } | null {
  if (!row || typeof row !== "object") return null;
  const rat = str(row.rationale);
  const d = /domain="([^"]*)"/.exec(rat);
  if (!d) return null;
  const v = /variable="([^"]*)"/.exec(rat);
  return { domain: d[1].trim(), variable: v ? v[1].trim() : "" };
}

/** Which legacy normaliser shape this row is (see the header), or null. */
function legacyNormaliserKind(row: Record<string, unknown>): "no_answer" | "invalid_domain" | null {
  if (str(row.scenario).toLowerCase() !== "not_submitted") return null;
  if (str(row.domain) && str(row.variable)) return null; // names a target now
  if (!str(row.rationale).includes(LEGACY_NORMALISER_RATIONALE)) return null;
  const orig = recordedMapperOutput(row);
  if (!orig) return null;
  if (orig.domain || orig.variable) return "invalid_domain";
  return str(row.source) === "ai" ? "no_answer" : null;
}

/** True when the mapper produced no usable answer for this row (new or legacy shape). */
export function isMappingFailedRow(row: RowLike): boolean {
  if (!row || typeof row !== "object") return false;
  const scenario = str(row.scenario).toLowerCase();
  if (scenario === MAPPING_FAILED_SCENARIO) return true;
  const flags = flagsOf(row);
  if (flags.includes(MAPPING_FAILED_FLAG) || flags.includes(INVALID_DOMAIN_FLAG)) return true;
  // Legacy: only the old failure branch combined not_submitted with no target
  // and this rationale. A row that names a target is not a failure.
  if (scenario === "not_submitted" && !(str(row.domain) && str(row.variable))) {
    if (str(row.rationale).includes(LEGACY_FAILED_RATIONALE)) return true;
  }
  // Legacy normaliser rows, decisions A and B (header).
  return legacyNormaliserKind(row) !== null;
}

/**
 * True when the row is a model answer whose domain is not a valid SDTM domain
 * (decision B): new rows by their flag, legacy rows by the normaliser's record
 * of a non-blank original. Always also a mapping failure (needs review).
 */
export function isInvalidDomainRow(row: RowLike): boolean {
  if (!row || typeof row !== "object") return false;
  if (flagsOf(row).includes(INVALID_DOMAIN_FLAG)) return true;
  return legacyNormaliserKind(row) === "invalid_domain";
}

/** The label a reader shows for a failed row: invalid-domain answers say so. */
export function mappingFailedLabel(row: RowLike): string {
  return isInvalidDomainRow(row) ? INVALID_DOMAIN_LABEL : MAPPING_FAILED_LABEL;
}

/** True when the row is the mapping model's not-submitted PROPOSAL (not a failure, not a decision). */
export function isModelProposedNotSubmittedRow(row: RowLike): boolean {
  if (!row || typeof row !== "object") return false;
  if (isMappingFailedRow(row)) return false;
  if (str(row.scenario).toLowerCase() !== "not_submitted") return false;
  return str(row.notSubmittedBy) === MODEL_PROPOSAL_MARK || flagsOf(row).includes(MODEL_PROPOSAL_FLAG);
}

/** True when the row is not submitted by DECISION (the scenario or origin says so): not a failure, not a model proposal. */
export function isDeliberateNotSubmittedRow(row: RowLike): boolean {
  if (!row || typeof row !== "object") return false;
  if (isMappingFailedRow(row)) return false;
  if (isModelProposedNotSubmittedRow(row)) return false;
  return str(row.scenario).toLowerCase() === "not_submitted" || str(row.origin).toUpperCase() === "NOT SUBMITTED";
}

/**
 * True when the row is meant to reach the SDTM datasets: neither a decided
 * not-submitted nor a failure. A failure is excluded not because it is not
 * submitted but because it names no target yet; it is reported, never dropped.
 */
export function isSubmittableRow(row: RowLike): boolean {
  if (!row || typeof row !== "object") return false;
  return !isMappingFailedRow(row) && !isModelProposedNotSubmittedRow(row) && !isDeliberateNotSubmittedRow(row);
}

/** A row a person must look at: its mapping failed, or the model only proposed leaving it out. */
export function needsMappingReview(row: RowLike): boolean {
  return isMappingFailedRow(row) || isModelProposedNotSubmittedRow(row);
}

/** The scenario a reader should see: `mapping_failed` for a failure (legacy rows included), else the stored value. */
export function effectiveScenario(row: RowLike): string | null {
  if (!row || typeof row !== "object") return null;
  if (isMappingFailedRow(row)) return MAPPING_FAILED_SCENARIO;
  const s = row.scenario;
  return s == null ? null : String(s);
}

/**
 * True when the generator may write this row's SDTM target back into the data
 * dictionary. A not-submitted row and a FAILED row both carry no target:
 * writing either back would blank the dictionary's own assignment, and for a
 * failure that erases the one value S3 would otherwise have compared
 * (checker, 2026-10-19: the write-back skip was untested).
 */
export function carriesWriteBackTarget(row: RowLike): boolean {
  if (!row || typeof row !== "object") return false;
  if (isMappingFailedRow(row)) return false;
  return str(row.scenario).toLowerCase() !== "not_submitted";
}

export interface MappingStateCounts {
  total: number;
  /** Rows with a target (or routed to SUPP--). */
  mapped: number;
  /** Rows not submitted by decision. */
  notSubmitted: number;
  /** Rows the model proposed as not submitted, awaiting confirmation. */
  modelProposals: number;
  /** Rows the mapper failed on. Never folded into notSubmitted. */
  mappingFailed: number;
}

export function countMappingStates(rows: ReadonlyArray<RowLike>): MappingStateCounts {
  const out: MappingStateCounts = { total: 0, mapped: 0, notSubmitted: 0, modelProposals: 0, mappingFailed: 0 };
  for (const r of rows ?? []) {
    if (!r) continue;
    out.total++;
    if (isMappingFailedRow(r)) out.mappingFailed++;
    else if (isModelProposedNotSubmittedRow(r)) out.modelProposals++;
    else if (isDeliberateNotSubmittedRow(r)) out.notSubmitted++;
    else out.mapped++;
  }
  return out;
}

export interface PanelMappingCounts {
  total: number;
  /** Mapped by the curated library or a rule. */
  library: number;
  /** Mapped by the model. */
  fallback: number;
  /** Left out by the operational-field pre-filter ("Skipped (Non-SDTM)"), counted ONLY here. */
  skipped: number;
  /** Not submitted by any other decision (the library, a person). */
  notSubmitted: number;
  /** The model's not-submitted proposals (Needs Review). */
  modelProposals: number;
  /** Failed mappings, legacy rows included (Needs Review). */
  failed: number;
}

/**
 * The SDTM panel's tiles. Each row is counted exactly once, so the six add up
 * to the total (lead, 2026-10-19: pre-filter rows had been counted both as
 * "Skipped" and as "Not Submitted", and failures as "Mapped (Fallback)").
 */
export function panelMappingCounts(rows: ReadonlyArray<RowLike>): PanelMappingCounts {
  const out: PanelMappingCounts = { total: 0, library: 0, fallback: 0, skipped: 0, notSubmitted: 0, modelProposals: 0, failed: 0 };
  for (const r of rows ?? []) {
    if (!r) continue;
    out.total++;
    if (isMappingFailedRow(r)) out.failed++;
    else if (isModelProposedNotSubmittedRow(r)) out.modelProposals++;
    else if (str(r.source) === "pre_filter") out.skipped++;
    else if (isDeliberateNotSubmittedRow(r)) out.notSubmitted++;
    else if (str(r.source) === "lookup") out.library++;
    else out.fallback++;
  }
  return out;
}

/** "CM.CMPRIOR (mapping failed)" for the review note. */
function unresolvedLabel(r: Record<string, unknown>): string {
  const field = `${str(r.formCode) || str(r.formName) || "?"}.${str(r.fieldName) || "?"}`;
  if (isInvalidDomainRow(r)) {
    const d = recordedMapperOutput(r)?.domain ?? "";
    const given = d ? `the model gave the domain "${d}", which is` : "the model gave no domain, which is";
    return `${field} (mapping failed: ${given} not a valid SDTM domain)`;
  }
  return `${field} (${isMappingFailedRow(r) ? "mapping failed" : "not submitted on the model's proposal, awaiting confirmation"})`;
}

/**
 * The visible statement a submission package carries when any CRF field is
 * absent from it for a reason nobody decided: its mapping failed, or the model
 * only proposed leaving it out. Null when there is none. Every field is named:
 * the package must never drop a field silently. Not a gate; a statement.
 */
export function sponsorReviewNote(rows: ReadonlyArray<RowLike>): string | null {
  const unresolved = (rows ?? []).filter((r): r is Record<string, unknown> => !!r && needsMappingReview(r));
  if (unresolved.length === 0) return null;
  const n = unresolved.length;
  return (
    `[SPONSOR REVIEW: ${n} CRF field${n === 1 ? " is" : "s are"} not in this package because ` +
    `${n === 1 ? "its" : "their"} SDTM mapping failed or awaits confirmation: ` +
    `${unresolved.map(unresolvedLabel).join("; ")}]`
  );
}
